// Input sanitization utilities
// Protects against XSS, script injection, and malformed input.
// All functions accept `unknown` so callers don't need to pre-check types.

/** Strip HTML tags and dangerous characters from text input. Always returns a string. */
export function sanitizeText(str: unknown): string {
  if (typeof str !== "string") return "";
  return str
    .replace(/<[^>]*>/g, "") // strip HTML tags
    .replace(/javascript:/gi, "") // strip javascript: URLs
    .replace(/on\w+\s*=/gi, "") // strip event handlers (onclick=, etc)
    .trim();
}

/** Keep only digits and a single decimal point — for money/cost inputs. Strips
 * letters, signs and scientific-notation characters that a browser's type="number"
 * lets through (notably Firefox/Safari, which keep typed letters visible even though
 * the reported value is empty). Always returns a string. */
export function sanitizeMoney(str: unknown): string {
  const s = String(str ?? "").replace(/[^0-9.]/g, "");
  const dot = s.indexOf(".");
  if (dot === -1) return s;
  // keep the first decimal point, drop any later ones
  return s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, "");
}

/** Sanitize for use in notes/comments (allow basic text, strip scripts). Always returns a string. */
export function sanitizeNote(str: unknown): string {
  if (typeof str !== "string") return "";
  return str
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "") // strip script tags
    .replace(/<[^>]*>/g, "") // strip all HTML
    .replace(/javascript:/gi, "")
    .trim();
}

/** Sanitize email address. Always returns a string. */
export function sanitizeEmail(str: unknown): string {
  if (!str || typeof str !== "string") return "";
  return str
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9@._+-]/g, "");
}

// ---- Email normalise + validate (docs/email-validation-plan.md §5) ----
// The single source of truth for how an email address is stored and checked.
// `sanitizeEmail` above stays as the character-strip helper; these are the gate.
// The Edge Functions keep a small mirror of these (they cannot import Vite modules),
// and the DB CHECK uses the same pattern — keep all three in step.

/** Trim + lowercase. The canonical stored form of an address. Always returns a string.
 *  JS `.trim()` also removes tabs, newlines and non-breaking spaces (U+00A0). */
export function normalizeEmail(str: unknown): string {
  if (typeof str !== "string") return "";
  return str.trim().toLowerCase();
}

/** Pragmatic "is this a sendable address" check (not full RFC-5322): one @, a
 *  non-empty local part, a domain with a dot, and no whitespace, angle brackets,
 *  commas or double quotes. Checks the normalised form. */
const EMAIL_RE = /^[^\s@<>,"]+@[^\s@<>,"]+\.[^\s@<>,"]+$/;
export function isValidEmail(str: unknown): boolean {
  return EMAIL_RE.test(normalizeEmail(str));
}

/** For OPTIONAL email fields: the normalised address, or `null` when blank —
 *  so "no email" is always stored as NULL, never as an empty string ''. */
export function toNullableEmail(str: unknown): string | null {
  const email = normalizeEmail(str);
  return email === "" ? null : email;
}

/** Sanitize phone number (Australian format). Always returns a string. */
export function sanitizePhone(str: unknown): string {
  if (!str || typeof str !== "string") return "";
  return str.replace(/[^0-9+\-\s()]/g, "").trim();
}

/**
 * Format phone number for display.
 * e.g. 0400999666 → "0400 999 666", 0733334444 → "07 3333 4444"
 * Always returns a string (empty string for non-string / empty input).
 *
 * `kind` forces a shape for a 10-digit number regardless of prefix (used by the
 * per-field Phone/Mobile inputs so each always formats to its intended pattern):
 *   - "landline" → "0N NNNN NNNN"  (2-4-4)
 *   - "mobile"   → "04NN NNN NNN"  (4-3-3)
 *   - undefined  → auto: 04… → mobile, else landline (the display default).
 */
export function formatPhone(str: unknown, kind?: "landline" | "mobile"): string {
  if (typeof str !== "string" || !str) return "";
  const digits = str.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("61")) {
    return formatPhone(`0${digits.slice(2)}`, kind);
  }
  if (digits.length === 10) {
    const asMobile = kind === "mobile" || (kind === undefined && digits.startsWith("04"));
    if (asMobile) {
      return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
    }
    return `${digits.slice(0, 2)} ${digits.slice(2, 6)} ${digits.slice(6)}`;
  }
  return str;
}

/** Sanitize a filename (for uploads). Always returns a non-empty string. */
export function sanitizeFilename(str: unknown): string {
  if (!str || typeof str !== "string") return "file";
  return str
    .replace(/[^a-zA-Z0-9._\-\s]/g, "_")
    .replace(/\s+/g, "_")
    .slice(0, 200);
}

/**
 * HTML-escape a value for safe interpolation into a raw HTML string (output ENCODING).
 * Defense-in-depth for the print/PDF sinks that assemble HTML by hand (F8) — applied at the sink
 * regardless of input sanitization. Accepts unknown; nullish / non-strings are stringified.
 */
export function escapeHtml(str: unknown): string {
  return String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Sanitize an object's string values (shallow). Non-string values are passed through unchanged. */
export function sanitizeObject<T extends Record<string, unknown>>(obj: T): T {
  if (!obj || typeof obj !== "object") return obj;
  const clean = {} as T;
  for (const key of Object.keys(obj) as Array<keyof T>) {
    const val = obj[key];
    (clean as Record<keyof T, unknown>)[key] = typeof val === "string" ? sanitizeText(val) : val;
  }
  return clean;
}
